import loadable from "@loadable/component";
import { inject, observer } from "mobx-react";
import React from "react";
import type { Store } from "../../../client/stores";
import { AppTray } from "../../core/chrome/AppTray.component";
import { Footer } from "./chrome/Footer.component";
import { Header } from "../../core/chrome/Header.component";
import { IHRPlayerBar } from "../../core/chrome/IHRPlayerBar.component";
import Page from "../../core/components/pages/Page.component";
import { Routes } from "../../core/components/Routes";
import { Title } from "../../core/components/Title.component";
import { Ad } from "../../core/page-blocks/Ad.component";
import { PageBlockRegistry } from "../../core/page-blocks/Block.component";
import Item from "../../core/page-blocks/content/Item.component";
import { ItemReference } from "../../core/page-blocks/content/ItemReference.component";
import { Html } from "../../core/page-blocks/Html.component";
import { ShowLoader } from "../../core/page-blocks/show/ShowLoader.component";
import { TemplateComponent } from "../TemplateComponent.component";
import PremierePushdownAd from "./chrome/PremierePushdownAd.component";
import "./index.style.scss";

const LegalNoticeLoader = loadable(() => import("../../core/page-blocks/legalnotice/LegalNoticeLoader.component"), {
    ssr: false,
});
const LeavingSiteLoader = loadable(() => import("./components/leaving-site/LeavingSiteLoader.component"), {
    ssr: false,
});

const AuthModal = loadable(() => import("../../core/components/AuthModal.component"), {
    ssr: false,
});

const Subscribe = loadable(() => import("../../core/page-blocks/newsletter/Subscribe.component"));
const Unsubscribe = loadable(() => import("../../core/page-blocks/newsletter/Unsubscribe.component"));

interface TemplateProps {
    store?: Store;
}

const pageComponent = Page;
export const pageComponents = {
    generic_80_20: pageComponent,
    generic_full: pageComponent,
    generic_80_20_with_title: pageComponent,
    detail: pageComponent,
    micro_detail: pageComponent,
    home: pageComponent,
    aggregate_full: pageComponent,
    microhome: pageComponent,
    microhome_rail: pageComponent,
    plaintext: pageComponent,
};

export const blockComponents: PageBlockRegistry = {
    // Bundled
    ad: () => Ad,
    calendar: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/calendar/CalendarTileLoader.component"))
            : loadable(() => import("../../core/page-blocks/calendar/CalendarLoader.component")),
    contact: () => loadable(() => import("../../core/page-blocks/contact/Contact.component")),
    contest: () => loadable(() => import("../../core/page-blocks/contest/ContestLoader.component")),
    contests: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/contests/ContestsTileLoader.component"), { ssr: false })
            : loadable(() => import("../../core/page-blocks/contests/ContestsLoader.component")),
    event: () => loadable(() => import("../../core/page-blocks/event/EventDetailLoader.component")),
    feed: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/datasource/DatasourceTileLoader.component"))
            : loadable(() => import("../../core/page-blocks/datasource/DatasourceLoader.component")),
    header: () => loadable(() => import("../../core/page-blocks/header/Header.component")),
    html: () => Html,
    item: () => Item,
    "item-reference": () => ItemReference,
    newslettersubscribe: () => Subscribe,
    newsletterunsubscribe: () => Unsubscribe,
    onair: () => loadable(() => import("../../core/components/tiles/OnairTileLoader.component")),
    onairschedule: () => loadable(() => import("../../core/components/onair/OnAirSchedule.component")), // This is for the /schedule page, so you don't get confused
    playlist: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/music/MusicTile.component"))
            : loadable(() => import("../../core/page-blocks/music/MusicPage.component")),
    podcasts: () => loadable(() => import("../../core/page-blocks/podcast/PodcastsLoader.component")),
    recommendation: () =>
        loadable(() => import("../../core/page-blocks/recommendation/Recommendation.component"), { ssr: false }),
    search: () => loadable(() => import("../../core/page-blocks/search/SearchLoader.component")),
    show: () => ShowLoader,
    title: () => Title,
    topsongs: () => loadable(() => import("../../core/page-blocks/music/MusicPage.component")),
    traffic: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/components/tiles/TrafficTile.component"))
            : loadable(() => import("../../core/page-blocks/traffic/Traffic.component")),
    weather: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/components/tiles/WeatherTileLoader.component"))
            : loadable(() => import("../../core/page-blocks/weather/WeatherLoader.component")),

    asset: () => loadable(() => import("../../core/page-blocks/asset/Asset.component")),
    chartlist: () => loadable(() => import("../../core/page-blocks/chartlist/ChartListPageLoader.component")),
    chart: () => loadable(() => import("../../core/page-blocks/chartlists/ChartListsCollectionPageLoader.component")),
    instagram: () => () => null,
    twitter: () => loadable(() => import("../../core/page-blocks/twitter/Twitter.component")),
    howToListen: () =>
        loadable(() => import("../../core/page-blocks/howToListen/HowToListenLoader.component"), {
            ssr: false,
        }),
    fcc: () => loadable(() => import("../../core/page-blocks/fccApplication/fccApplication.component")),
    youtube: () => loadable(() => import("../../core/page-blocks/youtubePlayer/YouTubePlayer.component")),
};

export const micrositeComponents: PageBlockRegistry = {
    ...blockComponents,
    about: () => loadable(() => import("../../core/components/microsite/MicrositeInfo.component")),
    feed: block =>
        block?.tags?.includes("display-hints/tile")
            ? loadable(() => import("../../core/page-blocks/datasource/MicrositeDatasourceTileLoader.component"))
            : loadable(() => import("../../core/page-blocks/datasource/DatasourceLoader.component")),
};

export const PremiereTemplate = inject("store")(
    observer(({ store }: TemplateProps) => {
        if (!store) {
            return null;
        }

        return (
            <TemplateComponent display="grid">
                <Routes
                    headerGroup={
                        <React.Fragment>
                            <PremierePushdownAd />
                            <Header />
                        </React.Fragment>
                    }
                    pageComponents={pageComponents}
                    blockComponents={store.microsite ? micrositeComponents : blockComponents}
                    footerGroup={
                        <React.Fragment>
                            <LeavingSiteLoader />
                            <Footer />
                            <AppTray />
                            <LegalNoticeLoader />
                            <AuthModal />
                        </React.Fragment>
                    }
                />
                <IHRPlayerBar />
            </TemplateComponent>
        );
    }),
);

export default PremiereTemplate;
